import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { WEB3FORMS_ACCESS_KEY } from "../config";

// Animation variants (shared, matches homepage)
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Animated Section wrapper (shared, matches homepage)
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Newsletter Signup Section - reused on Homepage and Newsletter page
export const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Newsletter Subscription - GSLC",
          from_name: "GSLC Newsletter",
          email: email,
          message: "New newsletter subscription request"
        }),
      });

      const result = await response.json();
      if (result.success) {
        toast.success("Successfully subscribed to our newsletter!");
        setEmail('');
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection className="section-padding">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="max-w-2xl mx-auto text-center">
          <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Stay Updated</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 mb-6 text-slate-800">
            Subscribe to Our <span className="gradient-text">Newsletter</span>
          </h2>
          <p className="text-slate-500 mb-8">Get the latest updates on learning forums, programs, and educational insights.</p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              required
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-white border-2 border-slate-200 rounded-full px-6 py-4 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="Enter your email"
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              data-testid="newsletter-subscribe-btn"
              disabled={isSubmitting}
              className="btn-gradient whitespace-nowrap disabled:opacity-50"
            >
              {isSubmitting ? "Subscribing..." : "Subscribe"}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};
